import { FC, memo, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  type TopicTreeNode,
  countTopicsInNode,
} from "../hooks/useBuildTopicTree";
import type { CachedTopic } from "../slice";

type TopicTreeProps = {
  nodes: TopicTreeNode[];
  onRemoveTopic: (topicId: string) => void;
  showLabel?: boolean;
  title?: string;
};

/**
 * Displays selected topics organized in a hierarchical tree structure.
 * Uses MUI Accordion components for expand/collapse functionality.
 */
export const TopicTree: FC<TopicTreeProps> = memo(
  ({
    nodes,
    onRemoveTopic,
    showLabel = true,
    title = "Topics by Collection",
  }) => {
    if (nodes.length === 0) return null;

    return (
      <Box sx={{ mt: 1 }}>
        {showLabel && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            {title}
          </Typography>
        )}
        {nodes.map((node) => (
          <TreeNode key={node.id} node={node} onRemoveTopic={onRemoveTopic} />
        ))}
      </Box>
    );
  }
);

TopicTree.displayName = "TopicTree";

type TreeNodeProps = {
  node: TopicTreeNode;
  onRemoveTopic: (topicId: string) => void;
  depth?: number;
};

const TreeNode: FC<TreeNodeProps> = memo(
  ({ node, onRemoveTopic, depth = 0 }) => {
    const [expanded, setExpanded] = useState(true);
    const topicCount = countTopicsInNode(node);
    const hasContent = node.topics.length > 0 || node.children.length > 0;

    const handleChange = useCallback(
      (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded);
      },
      []
    );

    if (!hasContent) return null;

    return (
      <Accordion
        expanded={expanded}
        onChange={handleChange}
        disableGutters
        elevation={0}
        sx={{
          "&:before": { display: "none" },
          bgcolor: "transparent",
          ml: depth * 2,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{
            minHeight: 36,
            px: 1,
            py: 0,
            "& .MuiAccordionSummary-content": {
              my: 0.5,
              alignItems: "center",
              gap: 1,
            },
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {node.displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({topicCount})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, py: 0.5 }}>
          {/* Topics directly under this collection */}
          {node.topics.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
              {node.topics.map((topic) => (
                <TopicChip
                  key={topic.id}
                  topic={topic}
                  onRemove={onRemoveTopic}
                />
              ))}
            </Box>
          )}
          {/* Child collections */}
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              onRemoveTopic={onRemoveTopic}
              depth={0}
            />
          ))}
        </AccordionDetails>
      </Accordion>
    );
  }
);

TreeNode.displayName = "TreeNode";

type TopicChipProps = {
  topic: CachedTopic;
  onRemove: (topicId: string) => void;
};

const TopicChip: FC<TopicChipProps> = memo(({ topic, onRemove }) => {
  const handleDelete = useCallback(() => {
    onRemove(topic.id);
  }, [topic.id, onRemove]);

  return (
    <Chip
      label={topic.displayName}
      size="small"
      onDelete={handleDelete}
      deleteIcon={
        <IconButton size="small" sx={{ p: 0.25 }}>
          <CloseIcon fontSize="inherit" />
        </IconButton>
      }
      sx={{ maxWidth: 200 }}
    />
  );
});

TopicChip.displayName = "TopicChip";
